// @flow
import React from 'react';
import { Form, Grid, Row, Col, Alert } from 'react-bootstrap';
import { Elements } from 'react-stripe-elements';
import { FadeLoader } from 'react-spinners';

import { connect } from 'react-redux';
import { setSubscriptionMethod } from '../../redux/actionCreators';

import Api from '../../api/api';

import type { PersonRegistrationData } from './PersonRegistrationForm';
import type { StripeCreditCardType, cardValidationStatusType } from '../../utils/types';
import { getJsonFromUrl } from '../../utils/helpers';

import PersonRegistrationDataForm from './PersonRegistrationForm';
import CreditCardForm from './CreditCardForm';
import EditEmail from './EditEmail';
import SuccessModal from './SuccessModal';

type Props = {
  history: Object,
  location: Object,
  payment_plan_type: string,
  subscription_method: string,
  showGiftForm: boolean,
  handleSubscriptionMethodSelect: string => void,
};

type State = {
  data: PersonRegistrationData,
  cardValidationStatus: cardValidationStatusType,
  creditCardErrorFields: Array<string>,
  showCreditCardForm: boolean,
  showEmailConfirmModal: boolean,
  showSuccessModal: boolean,
  errors: Array<Object>,
  loading: boolean,
  message: {
    type: string,
    text: string,
  },
};

class Register extends React.Component<Props, State> {
  state = {
    data: {
      sender_first_name: '',
      sender_last_name: '',
      sender_email: '',
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password_confirmation: '',
      message: '',
      agreedToTermsOfService: false,
      payment_plan_type: this.props.payment_plan_type,
      subscription_method: this.props.subscription_method,
      is_gift: false,
      card: {
        stripe_token: '',
      },
    },
    cardValidationStatus: {
      number: false,
      expiration_date: false,
      cvc: false,
      zipcode: false,
    },
    creditCardErrorFields: [],
    showCreditCardForm: false,
    showEmailConfirmModal: false,
    showSuccessModal: false,
    errors: [],
    loading: false,
    message: {
      type: 'success',
      text: '',
    },
  };

  componentDidMount() {
    // added to avoid page auto scroll to bottom on low-res screens (GIFT mode)
    window.scrollTo(0, 0);

    const {
      location: { search },
      subscription_method,
    } = this.props;

    if (search) {
      const requestData = getJsonFromUrl(this.props.location.search);

      if (requestData.confirmation_token) {
        return Api.users
          .verifyEmail(requestData.confirmation_token)
          .then(response => {
            const { email, first_name, last_name, subscription_method, is_gift } = response;

            this.setState({ data: { ...this.state.data, email, first_name, last_name, subscription_method, is_gift } });
          })
          .catch((error): void => {
            if (error.response.status === 422) {
              this.props.history.push('/invalid-link');
            }
          });
      }
    }

    if (!subscription_method) {
      const el = document.getElementById('subscription-plans-link');

      return el && el.click();
    }
  }

  componentWillUnmount() {
    this.props.handleSubscriptionMethodSelect('');
  }

  setMessage = (text: string = '', type: string = 'success') => {
    this.setState({ ...this.state, message: { text, type } });
  };

  handlePersonalDataChange = (data: PersonRegistrationData) => {
    this.setState({ ...this.state, data, showCreditCardForm: true });

    if (this.state.message.text) {
      this.setMessage();
    }
  };

  handleCreditCardDataChange = (e: SyntheticInputEvent<HTMLInputElement>, field: string): void => {
    // $FlowFixMe
    if (e.error && !this.state.creditCardErrorFields.includes(field)) {
      this.setState({ creditCardErrorFields: [...this.state.creditCardErrorFields, field] });
    }

    // $FlowFixMe
    if (e.complete) {
      const errorFields = this.state.creditCardErrorFields.slice();
      const index = errorFields.findIndex(error => error === field);

      index >= 0 && errorFields.splice(index, 1);

      this.setState({
        creditCardErrorFields: errorFields,
        cardValidationStatus: { ...this.state.cardValidationStatus, [field]: true },
      });
    }
  };

  handlePrevClick = () => this.setState({ showCreditCardForm: false, message: { text: '', type: 'success' } });

  formatUserData = data => {
    const user = Object.assign({}, data);

    if (this.props.showGiftForm) {
      user.is_gift = true;
      delete user.password;
      delete user.password_confirmation;
    } else {
      delete user.sender_first_name;
      delete user.sender_last_name;
      delete user.sender_email;
      delete user.message;
    }

    return user;
  };

  handleSubmit = (card: StripeCreditCardType): void => {
    this.setState({ ...this.state, data: { ...this.state.data, card } }, () => {
      const user = this.formatUserData(this.state.data);

      Api.users
        .register({ user })
        .then(() => {
          if (this.props.showGiftForm) {
            return this.toggleSuccessModal();
          }

          this.toggleSpinner();

          this.toggleEmailConfirmModal();
        })
        .catch(error => {
          const errors = Object.values(error);

          if (errors.length && typeof errors[0] === 'string') {
            this.setMessage(errors[0], 'danger');
            this.toggleSpinner();

            if (error.card) {
              this.setState({
                showCreditCardForm: false,
                cardValidationStatus: {
                  cvc: false,
                  expiration_date: false,
                  number: false,
                  zipcode: false,
                },
              });
            }
          }
        });
    });
  };

  toggleSpinner = () => this.setState(prevState => ({ loading: !prevState.loading }));

  toggleEmailConfirmModal = (): void => this.setState({ showEmailConfirmModal: !this.state.showEmailConfirmModal });

  toggleSuccessModal = (): void => this.setState(prevState => ({ showSuccessModal: !prevState.showSuccessModal }));

  render() {
    const {
      showCreditCardForm,
      data,
      cardValidationStatus,
      creditCardErrorFields,
      showEmailConfirmModal,
      showSuccessModal,
      loading,
      message,
    } = this.state;

    return (
      <Grid className={`main-fullheight started-laptop ${loading ? 'loading' : ''}`}>
        <div className="fullheight">
          {loading && (
            <div className="loader-container">
              <FadeLoader className="loader" color="#095674" loading={loading} />
            </div>
          )}
          <Row className="show-grid card-form-reg">
            <Col md={12} className="show-grid-in for-table-width">
              {message.text && (
                <Alert
                  bsStyle={message.type}
                  id="PaymentAlert"
                  className="alert-test"
                  onDismiss={() => this.setMessage('')}
                >
                  <span className="png-ok-circle" />
                  <p>{message.text}</p>
                </Alert>
              )}
              <h2 className="heading hd-left">Get Started</h2>
              <Form>
                {!showCreditCardForm ? (
                  <PersonRegistrationDataForm
                    data={data}
                    handleNavButtonClick={this.handlePersonalDataChange}
                    toggleSpinner={this.toggleSpinner}
                  />
                ) : (
                  <Elements>
                    <CreditCardForm
                      name={`${data.first_name} ${data.last_name}`}
                      cardValidationStatus={cardValidationStatus}
                      creditCardErrorFields={creditCardErrorFields}
                      toggleSpinner={this.toggleSpinner}
                      handlePrevClick={this.handlePrevClick}
                      handleCreditCardDataChange={this.handleCreditCardDataChange}
                      handleSubmitClick={this.handleSubmit}
                      setMessage={this.setMessage}
                    />
                  </Elements>
                )}
              </Form>
            </Col>
          </Row>

          <EditEmail show={showEmailConfirmModal} email={data.email} />

          <SuccessModal
            show={showSuccessModal}
            firstName={data.first_name}
            lastName={data.last_name}
            email={data.email}
          />
        </div>
      </Grid>
    );
  }
}

const mapStateToProps = state => ({
  payment_plan_type: state.user.payment_plan_type,
  subscription_method: state.registration.subscription_method,
  showGiftForm: state.registration.showGiftForm,
});

const mapDispatchToProps = dispatch => ({
  handleSubscriptionMethodSelect(subscription_method) {
    dispatch(setSubscriptionMethod(subscription_method));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Register);



// WEBPACK FOOTER //
// ./src/components/Register/Register.js